// src/pages/Employee/CreateEmployee/steps/Step5Qualifications.tsx
import React from 'react';
import { Row, Col, FormGroup, Label, Input, Button, Card, CardBody } from 'reactstrap';
import { Plus, Trash2, BookOpen, Briefcase, Award } from 'react-feather';
import {
  QualificationsData,
  StepProps,
  emptyEducation,
  emptyExperience,
  emptyCertification,
} from '../../types/employee.types';
import FileUploadField from './fileuploadfile';

const COURSE_TYPES = [
  { value: '1', label: 'SSLC / 10th' },
  { value: '2', label: 'HSC / 12th' },
  { value: '3', label: "Diploma" },
  { value: '4', label: "Bachelor's Degree" },
  { value: '5', label: "Master's Degree" },
  { value: '6', label: 'Other' },
];

const Step5Qualifications: React.FC<StepProps<QualificationsData>> = ({ data, onChange }) => {
  // ---- Education ----
  const addEducation = () => onChange({ ...data, educations: [...data.educations, emptyEducation()] });
  const removeEducation = (key: string) =>
    onChange({ ...data, educations: data.educations.filter((e) => e._key !== key) });
  const updateEducation = (key: string, field: string, value: string) =>
    onChange({
      ...data,
      educations: data.educations.map((e) => (e._key === key ? { ...e, [field]: value } : e)),
    });

  // ---- Experience ----
  const addExperience = () => onChange({ ...data, experiences: [...data.experiences, emptyExperience()] });
  const removeExperience = (key: string) =>
    onChange({ ...data, experiences: data.experiences.filter((e) => e._key !== key) });
  const updateExperience = (key: string, field: string, value: any) =>
    onChange({
      ...data,
      experiences: data.experiences.map((e) => (e._key === key ? { ...e, [field]: value } : e)),
    });

  // ---- Certifications ----
  const addCertification = () =>
    onChange({ ...data, certifications: [...data.certifications, emptyCertification()] });
  const removeCertification = (key: string) =>
    onChange({ ...data, certifications: data.certifications.filter((c) => c._key !== key) });
  const updateCertification = (key: string, field: string, value: any) =>
    onChange({
      ...data,
      certifications: data.certifications.map((c) => (c._key === key ? { ...c, [field]: value } : c)),
    });

  return (
    <div>
      {/* Education */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          <BookOpen size={16} />
          Education
        </h6>
        <Button color="primary" size="sm" type="button" onClick={addEducation}>
          <Plus size={14} className="me-1" />
          Add Education
        </Button>
      </div>
      {data.educations.length === 0 && (
        <div className="text-muted small fst-italic mb-3">No education records added yet.</div>
      )}
      {data.educations.map((edu, idx) => (
        <Card key={edu._key} className="mb-3 border">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold small text-muted">Education #{idx + 1}</span>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                type="button"
                onClick={() => removeEducation(edu._key)}
              >
                <Trash2 size={14} className="me-1" />
                Remove
              </Button>
            </div>
            <Row>
              <Col md={3}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Course Type</Label>
                  <Input
                    // size="sm"
                    type="select"
                    value={edu.course_type}
                    onChange={(e) => updateEducation(edu._key, 'course_type', e.target.value)}
                  >
                    <option value="">Select...</option>
                    {COURSE_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Course Name</Label>
                  <Input
                    // size="sm"
                    value={edu.course_name}
                    onChange={(e) => updateEducation(edu._key, 'course_name', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Institution / School</Label>
                  <Input
                    // size="sm"
                    value={edu.institution || edu.board_or_school}
                    onChange={(e) => updateEducation(edu._key, 'institution', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Percentage / CGPA</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={edu.percentage}
                    onChange={(e) => updateEducation(edu._key, 'percentage', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-0">
                  <Label className="small text-muted">From Year</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={edu.from_year}
                    onChange={(e) => updateEducation(edu._key, 'from_year', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-0">
                  <Label className="small text-muted">To Year</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={edu.to_year}
                    onChange={(e) => updateEducation(edu._key, 'to_year', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-0">
                  <Label className="small text-muted">Duration (months)</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={edu.duration_months}
                    onChange={(e) => updateEducation(edu._key, 'duration_months', e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      ))}

      {/* Experience */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-top pt-4">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          <Briefcase size={16} />
          Work Experience
        </h6>
        <Button color="primary" size="sm" type="button" onClick={addExperience}>
          <Plus size={14} className="me-1" />
          Add Experience
        </Button>
      </div>
      {data.experiences.length === 0 && (
        <div className="text-muted small fst-italic mb-3">No previous experience added. Skip if this is a fresher.</div>
      )}
      {data.experiences.map((exp, idx) => (
        <Card key={exp._key} className="mb-3 border">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold small text-muted">Experience #{idx + 1}</span>
              <Button
                color="link"
                // size="sm"
                className="p-0 text-danger"
                type="button"
                onClick={() => removeExperience(exp._key)}
              >
                <Trash2 size={14} className="me-1" />
                Remove
              </Button>
            </div>
            <Row>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Company Name</Label>
                  <Input
                    // size="sm"
                    value={exp.company_name}
                    onChange={(e) => updateExperience(exp._key, 'company_name', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">From Date</Label>
                  <Input
                    // size="sm"
                    type="date"
                    value={exp.from_date}
                    onChange={(e) => updateExperience(exp._key, 'from_date', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">To Date</Label>
                  <Input
                    // size="sm"
                    type="date"
                    value={exp.to_date}
                    onChange={(e) => updateExperience(exp._key, 'to_date', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Nature of Work</Label>
                  <Input
                    // size="sm"
                    value={exp.nature_of_work}
                    onChange={(e) => updateExperience(exp._key, 'nature_of_work', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Reason for Leaving</Label>
                  <Input
                    // size="sm"
                    value={exp.reason_for_leaving}
                    onChange={(e) => updateExperience(exp._key, 'reason_for_leaving', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FileUploadField
                  label="Experience Letter / Relieving Letter"
                  value={exp.attachment}
                  onChange={(v) => updateExperience(exp._key, 'attachment', v)}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      ))}

      {/* Certifications */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-top pt-4">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          <Award size={16} />
          Certifications
        </h6>
        <Button color="primary" size="sm" type="button" onClick={addCertification}>
          <Plus size={14} className="me-1" />
          Add Certification
        </Button>
      </div>
      {data.certifications.length === 0 && (
        <div className="text-muted small fst-italic mb-3">No certifications added. This section is optional.</div>
      )}
      {data.certifications.map((cert, idx) => (
        <Card key={cert._key} className="mb-3 border">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold small text-muted">Certification #{idx + 1}</span>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                type="button"
                onClick={() => removeCertification(cert._key)}
              >
                <Trash2 size={14} className="me-1" />
                Remove
              </Button>
            </div>
            <Row>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Course Name</Label>
                  <Input
                    // size="sm"
                    value={cert.course_name}
                    onChange={(e) => updateCertification(cert._key, 'course_name', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Institution</Label>
                  <Input
                    // size="sm"
                    value={cert.institution}
                    onChange={(e) => updateCertification(cert._key, 'institution', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Duration (months)</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={cert.duration_months}
                    onChange={(e) => updateCertification(cert._key, 'duration_months', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label className="small text-muted">Percentage</Label>
                  <Input
                    // size="sm"
                    type="number"
                    value={cert.percentage}
                    onChange={(e) => updateCertification(cert._key, 'percentage', e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FileUploadField
                  label="Certificate"
                  value={cert.attachment}
                  onChange={(v) => updateCertification(cert._key, 'attachment', v)}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

export default Step5Qualifications;